import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { buildWhatsAppLink } from '@/lib/api';
import SwipeGallery from './SwipeGallery';

export default function ProductCard({ product, index = 0, categoryMap = {} }) {
  const priceLabel = product.made_to_order && (!product.price || product.price === 0)
    ? 'On Enquiry'
    : `₹${Number(product.price).toLocaleString('en-IN')}`;

  const cat = categoryMap[product.category];
  const sub = cat?.subcategories?.find((s) => s.slug === product.subcategory);
  const categoryLabel = cat?.name || (product.category || '').replaceAll('-', ' ');
  const subLabel = sub?.name;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`product-card-${product.id}`}
      className="group flex flex-col"
    >
      <div className="relative border border-[color:var(--mb-border)] bg-[color:var(--mb-bg-alt)]">
        <SwipeGallery
          images={product.images}
          aspect="portrait"
          alt={product.name}
          className="w-full"
        />
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10 pointer-events-none">
          {product.featured && (
            <span className="eyebrow bg-[color:var(--mb-primary)] text-white px-2 py-1 rounded-full text-[0.55rem] tracking-[0.25em]">
              Featured
            </span>
          )}
          {product.made_to_order && (
            <span className="eyebrow bg-[color:var(--mb-text)] text-[color:var(--mb-bg)] px-2 py-1 rounded-full text-[0.55rem] tracking-[0.25em]">
              Made to Order
            </span>
          )}
        </div>
        {product.gender && product.gender !== 'unisex' && (
          <span className="absolute top-3 left-3 z-10 pointer-events-none opacity-0">
            {/* spacer to prevent conflict with gender badge below */}
          </span>
        )}
      </div>

      <div className="pt-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[0.6rem] tracking-[0.3em] uppercase text-[color:var(--mb-text-muted)] mb-1 truncate">
            {categoryLabel}{subLabel ? ` · ${subLabel}` : ''}{product.gender && product.gender !== 'unisex' ? ` · ${product.gender}` : ''}
          </p>
          <Link
            to={`/products/${product.id}`}
            className="font-serif-display text-xl sm:text-2xl leading-tight tracking-tight hover:text-[color:var(--mb-primary)]"
            style={{ transition: 'color 300ms ease' }}
          >
            {product.name}
          </Link>
        </div>
        <div className="text-right shrink-0">
          <p className="font-serif-display text-lg">{priceLabel}</p>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Link
          to={`/products/${product.id}`}
          data-testid={`product-view-${product.id}`}
          className="flex-1 text-center text-[0.65rem] tracking-[0.25em] uppercase border border-[color:var(--mb-text)]/25 text-[color:var(--mb-text)] py-2.5 rounded-full hover:border-[color:var(--mb-primary)] hover:text-[color:var(--mb-primary)]"
          style={{ transition: 'border-color 200ms ease, color 200ms ease' }}
        >
          View
        </Link>
        <a
          href={buildWhatsAppLink(product.name)}
          target="_blank"
          rel="noreferrer"
          data-testid={`product-enquire-${product.id}`}
          className="flex-1 inline-flex items-center justify-center gap-2 text-[0.65rem] tracking-[0.25em] uppercase bg-[#25D366] text-white py-2.5 rounded-full hover:brightness-95"
          style={{ transition: 'filter 200ms ease' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.52 3.48A11.87 11.87 0 0 0 12.06 0C5.5 0 .17 5.33.17 11.89c0 2.1.55 4.14 1.6 5.94L0 24l6.32-1.66a11.86 11.86 0 0 0 5.74 1.46h.01c6.56 0 11.89-5.34 11.89-11.9a11.83 11.83 0 0 0-3.44-8.42Zm-8.46 18.3h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.75.98 1-3.65-.24-.38a9.86 9.86 0 0 1-1.51-5.25c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.13 1.03 6.99 2.9a9.83 9.83 0 0 1 2.9 6.99c0 5.46-4.44 9.88-9.89 9.88Z"/></svg>
          Enquire
        </a>
      </div>
    </motion.div>
  );
}
